import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { MapPinIcon, PhoneIcon, ClockIcon, NavigationIcon, SearchIcon, FilterIcon, XIcon, MapIcon, ListIcon, CheckCircleIcon, MessageCircleIcon, HomeIcon, ChevronRightIcon } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
// Fix for default marker icons in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
interface Store {
  id: number;
  name: string;
  address: string;
  city: string;
  district: string;
  phone: string;
  coordinates: [number, number];
  hours: {
    open: string;
    close: string;
  };
  services: string[];
  isOpen: boolean;
}
export function StoreLocatorPage() {
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [showMap, setShowMap] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const stores: Store[] = [{
    id: 1,
    name: 'Innova Pharmacy - Riyadh Park',
    address: 'Riyadh Park Mall, Al Malaz District, Riyadh',
    city: 'Riyadh',
    district: 'Al Malaz',
    phone: '+966 11 234 5678',
    coordinates: [24.7136, 46.6753],
    hours: {
      open: '08:00',
      close: '23:00'
    },
    services: ['Prescription', 'Consultation', 'Home Delivery', '24/7'],
    isOpen: true
  }, {
    id: 2,
    name: 'Innova Pharmacy - Kingdom Centre',
    address: 'Kingdom Centre, Al Olaya District, Riyadh',
    city: 'Riyadh',
    district: 'Al Olaya',
    phone: '+966 11 234 5679',
    coordinates: [24.7114, 46.6747],
    hours: {
      open: '00:00',
      close: '23:59'
    },
    services: ['Prescription', 'Consultation', 'Home Delivery', '24/7', 'Lab Tests'],
    isOpen: true
  }, {
    id: 3,
    name: 'Innova Pharmacy - Red Sea Mall',
    address: 'Red Sea Mall, Al Rawdah District, Jeddah',
    city: 'Jeddah',
    district: 'Al Rawdah',
    phone: '+966 12 234 5678',
    coordinates: [21.5433, 39.1728],
    hours: {
      open: '09:00',
      close: '23:00'
    },
    services: ['Prescription', 'Consultation', 'Home Delivery'],
    isOpen: true
  }, {
    id: 4,
    name: 'Innova Pharmacy - Corniche',
    address: 'Corniche Road, Al Salama District, Jeddah',
    city: 'Jeddah',
    district: 'Al Salama',
    phone: '+966 12 234 5679',
    coordinates: [21.5169, 39.1748],
    hours: {
      open: '08:00',
      close: '22:00'
    },
    services: ['Prescription', 'Home Delivery', 'Lab Tests'],
    isOpen: false
  }, {
    id: 5,
    name: 'Innova Pharmacy - Al Faisaliyah',
    address: 'Al Faisaliyah District, Dammam',
    city: 'Dammam',
    district: 'Al Faisaliyah',
    phone: '+966 13 234 5678',
    coordinates: [26.4207, 50.0888],
    hours: {
      open: '08:00',
      close: '23:00'
    },
    services: ['Prescription', 'Consultation', 'Home Delivery', '24/7'],
    isOpen: true
  }, {
    id: 6,
    name: 'Innova Pharmacy - Al Corniche',
    address: 'Al Corniche District, Khobar',
    city: 'Khobar',
    district: 'Al Corniche',
    phone: '+966 13 234 5679',
    coordinates: [26.2885, 50.2085],
    hours: {
      open: '09:00',
      close: '22:00'
    },
    services: ['Prescription', 'Consultation', 'Home Delivery'],
    isOpen: true
  }];
  const cities = ['All Cities', 'Riyadh', 'Jeddah', 'Dammam', 'Khobar'];
  const allServices = ['Prescription', 'Consultation', 'Home Delivery', '24/7', 'Lab Tests'];
  const filteredStores = stores.filter(store => {
    const matchesSearch = store.name.toLowerCase().includes(searchQuery.toLowerCase()) || store.address.toLowerCase().includes(searchQuery.toLowerCase()) || store.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCity = selectedCity === 'all' || store.city === selectedCity;
    const matchesServices = selectedServices.length === 0 || selectedServices.every(service => store.services.includes(service));
    return matchesSearch && matchesCity && matchesServices;
  });
  const handleServiceToggle = (service: string) => {
    setSelectedServices(prev => prev.includes(service) ? prev.filter(s => s !== service) : [...prev, service]);
  };
  const handleGetDirections = (store: Store) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${store.coordinates[0]},${store.coordinates[1]}`;
    window.open(url, '_blank');
  };
  return <HelmetProvider data-id="element-9096" data-mp="pv0obaabdnxo">
      <SEO title="Store Locator" description="Find Innova Pharmacy branches across Saudi Arabia. See addresses, opening hours, phone numbers and maps. Fast delivery, pickup options, and 24/7 support." canonical="/en/store-locator/" ogImage="https://cdn.innovaksa.net/og/store-locator.jpg" hreflangAr="/store-locator/" hreflangEn="/en/store-locator/" hreflangDefault="/store-locator/" data-id="element-9097" data-mp="puhgimedofxd" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-9098" data-mp="23lgx33okgfk">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-9099" data-mp="3taoce6qjx8vl">
          <AnnouncementBar data-id="element-9100" data-mp="3tbzbvucyrugw" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-9101" data-mp="3tcsmj6f1n44b" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-9102" data-mp="1rkctuib1v9wk" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-9103" data-mp="28sdqi6do94q7">
          <h1 className="sr-only" data-id="element-9104" data-mp="2dr76q6kcf9u2">
            Store Locator - Find Innova Pharmacy Branches
          </h1>
          {/* Mobile Header Overlay - Only on mobile */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-9105" data-mp="1h30xt7ge1v9z">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-9106" data-mp="1l3kr1zxucequ" />
          </div>
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Store Locator'
        }]} title="Store Locator" description="Find Innova Pharmacy branches across Saudi Arabia with addresses, hours, and services." data-id="element-9107" data-mp="1v2aa3w1h458o" />
          <div className="container mx-auto px-4 md:px-8 lg:px-12 py-8" data-id="element-9108" data-mp="3nk60dw4gru4m">
            {/* Search and Filter Bar */}
            <div className="bg-white rounded-xl shadow-md p-4 md:p-6 mb-6" data-id="element-9109" data-mp="56os907tpr9m">
              <div className="flex flex-col md:flex-row gap-4" data-id="element-9110" data-mp="cc6gjivk3up">
                {/* Search Input */}
                <div className="flex-1 relative" data-id="element-9111" data-mp="f18be7pmx0r3">
                  <SearchIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-9112" data-mp="a3ylys28wp39" />
                  <input type="text" placeholder="Search by location, address, or city..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-9113" data-mp="o3pysph0zzfo" />
                </div>
                {/* City Filter */}
                <select value={selectedCity} onChange={e => setSelectedCity(e.target.value)} className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent md:w-48" data-id="element-9114" data-mp="15dbp80y51964">
                  {cities.map(city => <option key={city} value={city.toLowerCase().replace(' ', '-')} data-id="element-9115" data-mp="hlwjrzb2ebqn">
                      {city}
                    </option>)}
                </select>
                {/* Filter Button (Mobile) */}
                <button onClick={() => setShowFilters(!showFilters)} className="md:hidden flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-9116" data-mp="coej0w77b2tq">
                  <FilterIcon className="w-5 h-5" data-id="element-9117" data-mp="i29fki45wd6d" />
                  <span data-id="element-9118" data-mp="i1gh04fcwtzb">Filters</span>
                </button>
                {/* Map/List Toggle (Mobile) */}
                <button onClick={() => setShowMap(!showMap)} className="md:hidden flex items-center justify-center gap-2 px-4 py-3 bg-[#1E2B55] text-white rounded-lg hover:bg-[#162041]" data-id="element-9119" data-mp="mvkl8fx8mnwp">
                  {showMap ? <>
                      <ListIcon className="w-5 h-5" data-id="element-9120" data-mp="10r6cktqy1jsa" />
                      <span data-id="element-9121" data-mp="15raqqd4dq2fc">List</span>
                    </> : <>
                      <MapIcon className="w-5 h-5" data-id="element-9122" data-mp="rrszlr3m2d2t" />
                      <span data-id="element-9123" data-mp="vu664hq6mf0n">Map</span>
                    </>}
                </button>
              </div>
              {/* Service Filters */}
              <div className={`mt-4 pt-4 border-t border-gray-200 ${showFilters || window.innerWidth >= 768 ? 'block' : 'hidden'}`} data-id="element-9124" data-mp="3ch8noebg3nor">
                <div className="flex items-center gap-2 mb-3" data-id="element-9125" data-mp="3902z40bi05xn">
                  <FilterIcon className="w-4 h-4 text-gray-600" data-id="element-9126" data-mp="341t4bqtor7ck" />
                  <span className="text-sm font-medium text-gray-700" data-id="element-9127" data-mp="3419ja18qo2ub">
                    Filter by Services:
                  </span>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-9128" data-mp="3dzfnwqlxjn2v">
                  {allServices.map(service => <button key={service} onClick={() => handleServiceToggle(service)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedServices.includes(service) ? 'bg-[#1E2B55] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-9129" data-mp="2q9lbub9fq2jo">
                      {service}
                    </button>)}
                  {selectedServices.length > 0 && <button onClick={() => setSelectedServices([])} className="px-3 py-1.5 rounded-full text-sm font-medium text-red-600 hover:bg-red-50" data-id="element-9130" data-mp="22llkb94au841">
                      Clear All
                    </button>}
                </div>
              </div>
            </div>
            {/* Results Count */}
            <div className="mb-4 text-sm text-gray-600" data-id="element-9131" data-mp="360vvkxmopwmv">
              Showing{' '}
              <span className="font-semibold text-gray-900" data-id="element-9132" data-mp="29ad3ji18k1o5">
                {filteredStores.length}
              </span>{' '}
              of{' '}
              <span className="font-semibold text-gray-900" data-id="element-9133" data-mp="2dc6yrasnwdgx">
                {stores.length}
              </span>{' '}
              locations
            </div>
            {/* Map and Store List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9134" data-mp="2nbfp7tobj9yj">
              {/* Map Container */}
              <div className={`${showMap || window.innerWidth >= 768 ? 'block' : 'hidden'} md:block`} data-id="element-9135" data-mp="3tposawb4s8ij">
                <div className="bg-white rounded-xl shadow-md overflow-hidden h-[600px]" data-id="element-9136" data-mp="1n1odx2qtrfxn">
                  <MapContainer center={[24.7136, 46.6753]} zoom={6} style={{
                  height: '100%',
                  width: '100%'
                }} data-id="element-9137" data-mp="1n3ite1jbufuc">
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-9138" data-mp="18c0ey8648td3" />
                    {filteredStores.map(store => <Marker key={store.id} position={store.coordinates} eventHandlers={{
                    click: () => setSelectedStore(store)
                  }} data-id="element-9139" data-mp="udirmbk4hivp">
                        <Popup data-id="element-9140" data-mp="2jt01so9p2fzq">
                          <div className="p-2" data-id="element-9141" data-mp="2jpue9dgprm64">
                            <h3 className="font-bold text-gray-900 mb-1" data-id="element-9142" data-mp="2rsd5h3fk50aj">
                              {store.name}
                            </h3>
                            <p className="text-sm text-gray-600 mb-2" data-id="element-9143" data-mp="2wpn336d3oi9g">
                              {store.address}
                            </p>
                            <div className="flex items-center gap-2 mb-2" data-id="element-9144" data-mp="2msqu6zjahu4d">
                              <ClockIcon className="w-4 h-4 text-gray-500" data-id="element-9145" data-mp="292ezcsvblh9j" />
                              <span className="text-sm text-gray-700" data-id="element-9146" data-mp="2902arwv248ni">
                                {store.hours.open} - {store.hours.close}
                              </span>
                            </div>
                            <button onClick={() => handleGetDirections(store)} className="w-full bg-[#1E2B55] text-white text-sm font-medium py-2 rounded-lg hover:bg-[#162041]" data-id="element-9147" data-mp="24321ld2wups6">
                              Get Directions
                            </button>
                          </div>
                        </Popup>
                      </Marker>)}
                  </MapContainer>
                </div>
              </div>
              {/* Store List */}
              <div className={`${!showMap || window.innerWidth >= 768 ? 'block' : 'hidden'} md:block`} data-id="element-9148" data-mp="1kijy8outvuoo">
                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2" data-id="element-9149" data-mp="1ufg6qukq8gt8">
                  {filteredStores.map(store => <div key={store.id} className={`bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer ${selectedStore?.id === store.id ? 'ring-2 ring-[#1E2B55]' : ''}`} onClick={() => setSelectedStore(store)} data-id="element-9150" data-mp="1pfaf3eegiub5">
                      {/* Store Header */}
                      <div className="flex items-start justify-between mb-4" data-id="element-9151" data-mp="287346xqenvdl">
                        <div className="flex-1" data-id="element-9152" data-mp="2850aa2m4ti3l">
                          <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-9153" data-mp="15ugih813wyl3">
                            {store.name}
                          </h3>
                          <div className="flex items-center gap-2" data-id="element-9154" data-mp="1eu3h5m2gbqag">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${store.isOpen ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} data-id="element-9155" data-mp="1essdrncx4kjv">
                              <div className={`w-2 h-2 rounded-full ${store.isOpen ? 'bg-green-500' : 'bg-red-500'}`} data-id="element-9156" data-mp="1ti83r14mklyi" />
                              {store.isOpen ? 'Open Now' : 'Closed'}
                            </span>
                          </div>
                        </div>
                        <MapPinIcon className="w-6 h-6 text-[#1E2B55] flex-shrink-0" data-id="element-9157" data-mp="38endvoxwio7" />
                      </div>
                      {/* Store Details */}
                      <div className="space-y-3 mb-4" data-id="element-9158" data-mp="mf6jqz4dbr7">
                        <div className="flex items-start gap-2" data-id="element-9159" data-mp="nyr3trt68ys">
                          <MapPinIcon className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" data-id="element-9160" data-mp="3rz8iu617e5mz" />
                          <span className="text-sm text-gray-700" data-id="element-9161" data-mp="3n2i4po8ylk47">
                            {store.address}
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-9162" data-mp="jl9of8tvqsbl">
                          <PhoneIcon className="w-4 h-4 text-gray-500 flex-shrink-0" data-id="element-9163" data-mp="jinej2jio0q7" />
                          <a href={`tel:${store.phone}`} className="text-sm text-[#1E2B55] hover:underline" data-id="element-9164" data-mp="eju097weo5oz">
                            {store.phone}
                          </a>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-9165" data-mp="2uaye3ja193s6">
                          <ClockIcon className="w-4 h-4 text-gray-500 flex-shrink-0" data-id="element-9166" data-mp="2qaelmt0emocd" />
                          <span className="text-sm text-gray-700" data-id="element-9167" data-mp="tajzyvqx4py0">
                            {store.hours.open} - {store.hours.close}
                          </span>
                        </div>
                      </div>
                      {/* Services */}
                      <div className="mb-4" data-id="element-9168" data-mp="p98acq81o4pt">
                        <div className="flex flex-wrap gap-2" data-id="element-9169" data-mp="pa9o52gg35b8">
                          {store.services.map(service => <span key={service} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-[#1E2B55] rounded-full text-xs font-medium" data-id="element-9170" data-mp="1czm1meit1zjc">
                              <CheckCircleIcon className="w-3 h-3" data-id="element-9171" data-mp="187kmmku95upm" />
                              {service}
                            </span>)}
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex gap-3" data-id="element-9172" data-mp="20q829kqhchjb">
                        <button onClick={e => {
                      e.stopPropagation();
                      handleGetDirections(store);
                    }} className="flex-1 flex items-center justify-center gap-2 bg-[#1E2B55] text-white font-medium py-2.5 rounded-lg hover:bg-[#162041] transition-colors" data-id="element-9173" data-mp="2a25i6zkjraob">
                          <NavigationIcon className="w-4 h-4" data-id="element-9174" data-mp="1r54bnabch2i9" />
                          Get Directions
                        </button>
                        <button onClick={e => {
                      e.stopPropagation();
                      window.location.href = `tel:${store.phone}`;
                    }} className="flex items-center justify-center gap-2 border-2 border-[#1E2B55] text-[#1E2B55] font-medium px-4 py-2.5 rounded-lg hover:bg-[#F1F2F6] transition-colors" data-id="element-9175" data-mp="1n53v9k4oqh8k">
                          <PhoneIcon className="w-4 h-4" data-id="element-9176" data-mp="biznae9ddvdb" />
                        </button>
                      </div>
                    </div>)}
                  {filteredStores.length === 0 && <div className="bg-white rounded-xl shadow-md p-12 text-center" data-id="element-9177" data-mp="kjedns16y0no">
                      <MapPinIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-9178" data-mp="gfyeop1nfo7t" />
                      <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-9179" data-mp="ug0um5qec7kf">
                        No locations found
                      </h3>
                      <p className="text-gray-600 mb-6" data-id="element-9180" data-mp="phfvyaqslvy3">
                        Try adjusting your search or filter criteria
                      </p>
                      {/* Contact Customer Service Section */}
                      <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-9181" data-mp="z9vcd3mfycgv">
                        <p className="text-sm text-gray-700 mb-4 font-medium" data-id="element-9182" data-mp="1d7jwg98opfkv">
                          Need help finding a location?
                        </p>
                        <p className="text-sm text-gray-600 mb-4" data-id="element-9183" data-mp="1rtpo5n702kqy">
                          Contact our customer service team for assistance
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-9184" data-mp="1itayycqx0li6">
                          <a href="tel:+966112345678" className="inline-flex items-center justify-center gap-2 bg-[#1E2B55] text-white font-medium px-6 py-3 rounded-lg hover:bg-[#162041] transition-colors" data-id="element-9185" data-mp="1ivnc8clqa7ow">
                            <PhoneIcon className="w-5 h-5" data-id="element-9186" data-mp="1dwaprsyrr2mu" />
                            Call Us
                          </a>
                          <a href="https://wa.me/966112345678" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-medium px-6 py-3 rounded-lg hover:bg-[#20BA5A] transition-colors" data-id="element-9187" data-mp="14450iqzwizbc">
                            <MessageCircleIcon className="w-5 h-5" data-id="element-9188" data-mp="2s2fjpwcj943s" />
                            WhatsApp
                          </a>
                        </div>
                      </div>
                    </div>}
                </div>
              </div>
            </div>
          </div>
        </main>
        <div className="hidden md:block" data-id="element-9189" data-mp="3fs06yrfpyniv">
          <Footer data-id="element-9190" data-mp="3fpnq6hlp6j7e" />
        </div>
        <MobileFooterNav data-id="element-9191" data-mp="3asx0d2k3c98s" />
        <PharmacistChat data-id="element-9192" data-mp="3atq9ga6ivau3" />
        <div className="h-16 md:hidden" data-id="element-9193" data-mp="3atgiaj9qqxmt"></div>
        {/* Location Modal */}
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-9194" data-mp="36q0khjlxqr8j" />
      </div>
    </HelmetProvider>;
}